import clsx from 'clsx';
import { ProductType, RegisterMode, isKorpoRegisterMode, isTransferRegisterMode } from 'cart-model';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProductWizardState from '../../model/ProductWizardState';
import { productWizardActions } from '../../model/store';
import IkzeExtendedEntrepreneurDepositDialog
  from '../IkzeExtendedEntrepreneurDeposit/IkzeExtendedEntrepreneurDepositDialog';
import IkeOrIkzeMobileChooseDialog from './IkeOrIkzeMobileChooseDialog';
import { IkeOrIkzeLimits } from './IkeOrIkze';
import { Typography, Button } from 'ui-components';
import MobileComparisonRow from './MobileComparisonRow';
import MobileAdditionalActionsRow from './MobileAdditionalActionsRow';
import { useRetirementProductConfigs } from './retirementProductConfigs';

import useStyles from './IkeOrIkzeMobile.style';

const IkeOrIkzeMobile: React.FC<{ limits: IkeOrIkzeLimits }> = ({ limits }) => {
  const { t } = useTranslation('ike-or-ikze');
  const currentRegisterMode = useSelector((state: ProductWizardState) => state.cartState.registerMode);
  const isCurrentCartKorpo = isKorpoRegisterMode(currentRegisterMode);
  const isCurrentCartTransfer = isTransferRegisterMode(currentRegisterMode);
  const dispatch = useDispatch();
  const c = useStyles({});

  const { ikeConfig, ikzeConfig } = useRetirementProductConfigs();

  const setProductType = (value: ProductType, registerMode: RegisterMode): void => {
    dispatch(productWizardActions.setProductType(value));
    if (isCurrentCartKorpo || isCurrentCartTransfer) {
      dispatch(productWizardActions.clearCart());
    }
    dispatch(productWizardActions.setKorpoData(undefined));
    dispatch(productWizardActions.setTransferInstitutionName(undefined));
    dispatch(productWizardActions.setSelectedTypeOfInvestment({selectedTypeOfInvestment: value}));
    dispatch(productWizardActions.setRegisterMode(registerMode));
    dispatch(productWizardActions.setIsPensionProcess(true));
  };

  const containerRef = useRef<HTMLDivElement>(document.createElement('div'));

  const [bodyOffset, setBodyOffset] = useState(
    document.body.getBoundingClientRect(),
  );
  const [, setScrollY] = useState(bodyOffset.top);

  const [ikzeDialogOpen, setIkzeDialogOpen] = useState(false);
  const [confirmDialogProductType, setConfirmDialogProductType] = useState<ProductType | undefined>(undefined);

  const listener = (e: Event) => {
    setBodyOffset(document.body.getBoundingClientRect());
    setScrollY(bodyOffset.top);
  };

  useLayoutEffect(() => {
    window.addEventListener('scroll', listener);
    return () => {
      window.removeEventListener('scroll', listener);
    };
  });

  const conditionFixedMenu = (_.get(containerRef, 'current.offsetTop') as number + 135 < -bodyOffset.top)
                            ? c.fixedMenuActive
                            : c.fixedMenu;

  const comparisonRows = [
    {
      categoryKey: 'box.annual_deposit_limit',
      renderIkeContent: () => ikeConfig.renderDepositLimit(limits),
      renderIkzeContent: () => ikzeConfig.renderDepositLimit(limits, () => setIkzeDialogOpen(true)),
      hasBackground: false
    },
    {
      categoryKey: 'box.tax_benefits',
      renderIkeContent: () => ikeConfig.renderTaxBenefits(),
      renderIkzeContent: () => ikzeConfig.renderTaxBenefits(limits),
      hasBackground: true
    },
    {
      categoryKey: 'box.fee_for_cancellation',
      renderIkeContent: () => t('ike.fee_for_cancellation'),
      renderIkzeContent: () => t('ikze.fee_for_cancellation'),
      hasBackground: false
    },
    {
      categoryKey: 'box.payment_of_funds_without_consequences',
      renderIkeContent: () => t('ike.payment_of_funds_without_consequences'),
      renderIkzeContent: () => t('ikze.payment_of_funds_without_consequences'),
      hasBackground: true
    },
    {
      categoryKey: 'box.earlier_withdrawal_of_funds',
      renderIkeContent: () => t('ike.earlier_withdrawal_of_funds'),
      renderIkzeContent: () => t('ikze.earlier_withdrawal_of_funds'),
      hasBackground: false
    }
  ];

  return (
    <>
      <div className={conditionFixedMenu} >
        <div className={c.headerCell}><Typography variant="caption" component="h5" fontWeight="extraBold">{t('ike.title')}</Typography></div>
        <div className={c.headerCell}><Typography variant="caption" component="h5" fontWeight="extraBold" className={c.rightElementWithBorder}>{t('ikze.title')}</Typography></div>
      </div>
      <div className={c.tableRoot}>
        <div className={c.tableHead}>
          <div className={c.headerCell}><Typography variant="caption" component="h5" fontWeight="extraBold">{t('ike.title')}</Typography></div>
          <div className={c.headerCell}><Typography variant="caption" component="h5" className={c.rightElementWithBorder} fontWeight="extraBold">{t('ikze.title')}</Typography></div>
        </div>

        <div>
          {comparisonRows.map(row => (
            <MobileComparisonRow
              key={row.categoryKey}
              categoryKey={row.categoryKey}
              ikeContent={row.renderIkeContent()}
              ikzeContent={row.renderIkzeContent()}
              hasBackground={row.hasBackground}
            />
          ))}
          <MobileAdditionalActionsRow
            ikeConfig={ikeConfig}
            ikzeConfig={ikzeConfig}
            onProductTypeChange={setProductType}
          />
        </div>
      </div>
      <div className={c.fixedMenuBottom}>
        <div className={c.chooseButtonCell}>
          <Button className={c.chooseButton} onClick={() => setConfirmDialogProductType(ProductType.ike)}>
            {t('ike.button_mobile_choose')}
          </Button>
        </div>
        <div className={c.chooseButtonCell}>
          <Button className={clsx(c.buttonRight, c.chooseButton)} onClick={() => setConfirmDialogProductType(ProductType.ikze)}>
            {t('ikze.button_mobile_choose')}
          </Button>
        </div>
      </div>
      <IkzeExtendedEntrepreneurDepositDialog onClose={() => setIkzeDialogOpen(false)} open={ikzeDialogOpen} />
      <IkeOrIkzeMobileChooseDialog onClose={() => setConfirmDialogProductType(undefined)}
        selectedProductType={confirmDialogProductType}
        open={confirmDialogProductType !== undefined} />
    </>
  );
};

  export default IkeOrIkzeMobile;
